// store/middleware/ai-card-alignment.ts
//
// Cards the agent builds are top-aligned (Dawid, 2026-10-11: "every time we
// create array, cards it's top aligned so we never misalign the items").
//
// A card is a set in an Array section, and a set's default vertical alignment
// is centre: in a row of cards of equal height, a card with less text has its
// content pushed down and its heading no longer lines up with its neighbours'.
// The agent writes cards from many places — blueprints, library layouts,
// redesign restructures, research slides, added sections — so the rule sits
// where they all pass: a slide or section written while an AI undo batch is
// open (history.activeAiActionId — every agent write opens one first). The
// user's own inserts and edits, outside a batch, are never touched, and nor
// is a slide from their saved slides (`keepOwnSettings`).

import type { Middleware } from "@reduxjs/toolkit";

type Nested = {
  type?: string;
  settings?: Record<string, unknown>;
  children?: Nested[];
};
type Flat = {
  type?: string;
  settings?: Record<string, unknown>;
  childIds?: string[];
};

const isCardSection = (
  node: { type?: string; settings?: Record<string, unknown> } | undefined,
): boolean => node?.type === "combo" && node.settings?.name === "Array";

const topAligned = <T extends { settings?: Record<string, unknown> }>(
  node: T,
): T =>
  node.settings?.verticalAlignment === "start"
    ? node
    : {
        ...node,
        settings: { ...(node.settings ?? {}), verticalAlignment: "start" },
      };

/** `tree` with every card section and its cards top-aligned; the same object when none needed it. */
export const topAlignNestedCards = <T extends Nested>(tree: T): T => {
  const visit = (node: Nested, inCards: boolean): Nested => {
    const cards = isCardSection(node);
    const children =
      node.children?.map((c) => visit(c, cards)) ?? node.children;
    const childrenChanged =
      !!children && children.some((c, i) => c !== node.children?.[i]);
    const self =
      cards || (inCards && node.type === "set") ? topAligned(node) : node;
    return childrenChanged ? { ...self, children } : self;
  };
  return visit(tree, false) as T;
};

/** The same for a flat entity map (`createEntity` payloads). */
export const topAlignFlatCards = <M extends Record<string, Flat>>(
  entities: M,
): M => {
  const cardIds = new Set<string>();
  for (const [id, e] of Object.entries(entities)) {
    if (!isCardSection(e)) continue;
    cardIds.add(id);
    for (const child of e.childIds ?? [])
      if (entities[child]?.type === "set") cardIds.add(child);
  }
  if (cardIds.size === 0) return entities;
  let out: Record<string, Flat> | null = null;
  for (const id of Array.from(cardIds)) {
    const next = topAligned(entities[id]);
    if (next !== entities[id]) {
      out ??= { ...entities };
      out[id] = next;
    }
  }
  return (out ?? entities) as M;
};

type AnyAction = { type: string; payload?: Record<string, unknown> };

export const aiCardAlignment: Middleware = (api) => (next) => (action) => {
  const a = action as AnyAction;
  if (a.type !== "story/createEntity" && a.type !== "story/changeEntityLayout")
    return next(action);
  const state = api.getState() as {
    story?: { history?: { activeAiActionId?: string | null } };
  };
  if (!state.story?.history?.activeAiActionId || !a.payload)
    return next(action);
  // A slide put in from the team's or the user's own saved slides keeps the
  // settings they made it with (Dawid, 2026-10-13: "We can't alter alignment
  // for the saved slides, we should use the settings that were defined
  // there").
  if (a.payload.keepOwnSettings) return next(action);
  if (a.type === "story/changeEntityLayout" && a.payload.changed) {
    const changed = topAlignNestedCards(a.payload.changed as Nested);
    return next(
      changed === a.payload.changed
        ? action
        : { ...a, payload: { ...a.payload, changed } },
    );
  }
  if (a.type === "story/createEntity" && a.payload.entities) {
    const entities = topAlignFlatCards(
      a.payload.entities as Record<string, Flat>,
    );
    return next(
      entities === a.payload.entities
        ? action
        : { ...a, payload: { ...a.payload, entities } },
    );
  }
  return next(action);
};
